import React, { useState } from "react";
import { FileText, AlertTriangle, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { SITE } from "../../site.config";

export const EURO_CAUTION =
  "Please note: Powerz Auto Electrical is selective with European vehicles (BMW, Audi, Mercedes, VW, etc.). We'll assess each Euro job individually and may refer complex factory-system work to a specialist.";

const EMPTY = { firstName: "", lastName: "", phone: "", email: "", vehicle: "", details: "" };

const VARIANTS = {
  solid:
    "inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-white transition-colors clip-perf",
  outline:
    "inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-5 bebas text-xl tracking-[0.2em] hover:border-cyan-pz hover:text-cyan-pz transition-colors",
  header:
    "hidden xl:inline-flex whitespace-nowrap items-center gap-2 text-cyan-pz px-3 py-3 bebas text-base tracking-widest hover:text-white transition-colors",
  mobile:
    "mt-3 inline-flex items-center justify-center gap-2 border border-cyan-pz/60 text-cyan-pz px-5 py-4 bebas text-xl tracking-widest w-full",
  small:
    "inline-flex items-center justify-center gap-3 border border-cyan-pz/50 text-cyan-pz px-6 py-4 bebas text-base tracking-[0.2em] hover:bg-cyan-pz hover:text-black transition-colors",
};

const FIELD =
  "w-full bg-black border border-white/15 focus:border-cyan-pz outline-none text-white barlow px-4 py-3 text-base placeholder:text-white/30 transition-colors";

function validate(f) {
  const e = {};
  if (!f.firstName.trim()) e.firstName = "First name is required";
  if (!f.lastName.trim()) e.lastName = "Last name is required";
  if (!f.phone.trim() && !f.email.trim()) e.contact = "Enter a phone number or email so we can reach you";
  if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Enter a valid email address";
  if (f.details.trim().length < 10) e.details = "Tell us a bit more about the job (at least 10 characters)";
  return e;
}

function buildMailto(f, context) {
  const subject = `Quote Request — ${f.firstName} ${f.lastName}${context ? ` — ${context}` : ""}`;
  const body = [
    `Name: ${f.firstName} ${f.lastName}`,
    `Phone: ${f.phone || "—"}`,
    `Email: ${f.email || "—"}`,
    `Vehicle: ${f.vehicle || "—"}`,
    context ? `Service: ${context}` : null,
    "",
    "Job details:",
    f.details,
    "",
    "— Sent via the Get a Quote form at powerzautoelectrical.com.au",
  ]
    .filter((l) => l !== null)
    .join("\n");
  return `mailto:${SITE.business.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export default function QuoteButton({ variant = "outline", label = "GET A QUOTE", context = "", testId = "get-quote-btn", className = "" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    window.location.href = buildMailto(form, context);
    setSent(true);
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (!v) {
      setErrors({});
      setSent(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testId} className={`${VARIANTS[variant]} ${className}`}>
        <FileText className={variant === "header" || variant === "small" ? "w-4 h-4" : "w-5 h-5"} />
        {label}
      </button>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="quote-dialog"
          className="bg-[#0A0A0A] border border-cyan-pz/40 text-white rounded-none max-w-2xl max-h-[92vh] overflow-y-auto p-6 md:p-8"
        >
          <DialogHeader className="text-left">
            <p className="bebas text-cyan-pz tracking-[0.3em] text-xs mb-1">QUOTE REQUEST</p>
            <DialogTitle className="bebas text-white text-4xl md:text-5xl leading-[0.9] tracking-wide">
              GET A <span className="text-cyan-pz">QUOTE.</span>
            </DialogTitle>
            <DialogDescription className="text-white/60 barlow text-sm md:text-base pt-2">
              Fill in the details below and hit send — it'll open your email app with everything pre-filled, addressed to{" "}
              <span className="text-white">{SITE.business.email}</span>. We reply next business day.
            </DialogDescription>
          </DialogHeader>

          {sent ? (
            <div data-testid="quote-sent" className="mt-6 border border-cyan-pz/40 bg-cyan-pz/5 p-6">
              <p className="bebas text-cyan-pz text-2xl tracking-wider mb-2">YOUR EMAIL IS READY TO SEND</p>
              <p className="text-white/70 barlow text-sm">
                Your email app should have opened with your quote request. Just hit send. If nothing opened, email us directly at{" "}
                <a href={`mailto:${SITE.business.email}`} className="text-cyan-pz underline">{SITE.business.email}</a>{" "}
                or call {SITE.business.phone}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="mt-6 space-y-4" data-testid="quote-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="bebas text-white/60 tracking-widest text-xs block mb-1.5" htmlFor="q-first">FIRST NAME *</label>
                  <input id="q-first" data-testid="quote-first-name" className={FIELD} value={form.firstName} onChange={set("firstName")} placeholder="Zac" autoComplete="given-name" />
                  {errors.firstName && <p data-testid="quote-error-first-name" className="text-red-400 text-xs barlow mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="bebas text-white/60 tracking-widest text-xs block mb-1.5" htmlFor="q-last">LAST NAME *</label>
                  <input id="q-last" data-testid="quote-last-name" className={FIELD} value={form.lastName} onChange={set("lastName")} placeholder="Smith" autoComplete="family-name" />
                  {errors.lastName && <p data-testid="quote-error-last-name" className="text-red-400 text-xs barlow mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="bebas text-white/60 tracking-widest text-xs block mb-1.5" htmlFor="q-phone">PHONE *</label>
                  <input id="q-phone" data-testid="quote-phone" type="tel" className={FIELD} value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" autoComplete="tel" />
                </div>
                <div>
                  <label className="bebas text-white/60 tracking-widest text-xs block mb-1.5" htmlFor="q-email">EMAIL *</label>
                  <input id="q-email" data-testid="quote-email" type="email" className={FIELD} value={form.email} onChange={set("email")} placeholder="you@example.com" autoComplete="email" />
                  {errors.email && <p data-testid="quote-error-email" className="text-red-400 text-xs barlow mt-1">{errors.email}</p>}
                </div>
              </div>
              {errors.contact && <p data-testid="quote-error-contact" className="text-red-400 text-xs barlow -mt-2">{errors.contact}</p>}
              <p className="text-white/40 text-xs barlow -mt-2">* Phone or email required — give us at least one way to reach you.</p>

              <div>
                <label className="bebas text-white/60 tracking-widest text-xs block mb-1.5" htmlFor="q-vehicle">VEHICLE (MAKE / MODEL / YEAR)</label>
                <input id="q-vehicle" data-testid="quote-vehicle" className={FIELD} value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. 2021 Toyota HiLux SR5" />
              </div>

              <div>
                <label className="bebas text-white/60 tracking-widest text-xs block mb-1.5" htmlFor="q-details">JOB DETAILS *</label>
                <textarea id="q-details" data-testid="quote-details" rows={5} className={`${FIELD} resize-y`} value={form.details} onChange={set("details")} placeholder="What do you need done? e.g. REDARC Tow-Pro install, dual battery under bonnet, A/C not blowing cold..." />
                {errors.details && <p data-testid="quote-error-details" className="text-red-400 text-xs barlow mt-1">{errors.details}</p>}
              </div>

              <div data-testid="quote-euro-caution" className="flex items-start gap-3 border border-amber-400/40 bg-amber-400/5 p-4">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-amber-100/80 barlow text-sm leading-relaxed">{EURO_CAUTION}</p>
              </div>

              <button
                type="submit"
                data-testid="quote-submit-btn"
                className="w-full inline-flex items-center justify-center gap-3 bg-cyan-pz text-black px-8 py-5 bebas text-xl tracking-[0.2em] hover:bg-white transition-colors clip-perf"
              >
                <Send className="w-5 h-5" />
                SEND QUOTE REQUEST
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
